<script setup lang="ts">
import { Check as CheckIcon, Search as SearchIcon, X as XIcon } from '@lucide/vue'
import { computed, ref, watch } from 'vue'

import BaseInput from '@/components/ui/BaseInput.vue'
import MobileActionSheet from '@/components/ui/MobileActionSheet.vue'
import { tourTypeOptionGroups } from '@/utils/tourTypeOptions'

defineOptions({ name: 'MobileActivityTypeActionSheet' })

const KEYBOARD_OPEN_LIFT_PX = 200

const props = withDefaults(
  defineProps<{
    open: boolean
    currentType?: string | null
    saving?: boolean
    error?: string
  }>(),
  {
    currentType: null,
    saving: false,
    error: '',
  },
)

const emit = defineEmits<{
  close: []
  confirm: [type: string]
}>()

const query = ref('')
const draftType = ref('')

watch(
  () => [props.open, props.currentType] as const,
  ([open, currentType]) => {
    if (!open) return
    query.value = ''
    draftType.value = currentType ?? ''
  },
  { immediate: true },
)

const normalizedQuery = computed(() => query.value.trim().toLocaleLowerCase())
const currentTypeId = computed(() => props.currentType ?? '')
const currentOption = computed(() => {
  if (!currentTypeId.value) return { id: '', name: 'Activity' }

  for (const group of tourTypeOptionGroups) {
    const option = group.options.find((candidate) => candidate.id === currentTypeId.value)
    if (option) return option
  }

  return { id: currentTypeId.value, name: currentTypeId.value }
})
const filteredGroups = computed(() => {
  return tourTypeOptionGroups
    .map((group) => ({
      ...group,
      options: group.options.filter(
        (option) =>
          option.id !== currentTypeId.value &&
          (!normalizedQuery.value ||
            option.name.toLocaleLowerCase().includes(normalizedQuery.value) ||
            group.name.toLocaleLowerCase().includes(normalizedQuery.value)),
      ),
    }))
    .filter((group) => group.options.length > 0)
})
const showGenericActivity = computed(
  () =>
    currentTypeId.value !== '' &&
    (!normalizedQuery.value || 'activity'.includes(normalizedQuery.value)),
)

const selectType = (type: string) => {
  if (props.saving) return
  draftType.value = type
}
</script>

<template>
  <MobileActionSheet
    :open="open"
    title="Edit activity type"
    :keyboard-open-lift-px="KEYBOARD_OPEN_LIFT_PX"
    @close="emit('close')"
  >
    <template #header-left>
      <button
        type="button"
        class="flex h-9 w-9 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 hover:text-gray-900 disabled:opacity-60"
        :disabled="saving"
        aria-label="Cancel activity type edit"
        @click="emit('close')"
      >
        <XIcon class="h-4 w-4" />
      </button>
    </template>

    <template #header-right>
      <button
        type="button"
        class="flex h-9 w-9 items-center justify-center rounded-full border border-emerald-200 bg-white text-emerald-700 transition hover:bg-emerald-50 disabled:opacity-60"
        :disabled="saving"
        aria-label="Confirm activity type"
        @click="emit('confirm', draftType)"
      >
        <CheckIcon class="h-4 w-4" />
      </button>
    </template>

    <div class="flex min-h-0 flex-1 flex-col">
      <div class="shrink-0 px-4 pb-3 pt-3">
        <div class="relative">
          <SearchIcon
            class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          />
          <BaseInput
            v-model="query"
            class="pl-9"
            placeholder="Search activity types"
            aria-label="Search activity types"
            autocomplete="off"
            :disabled="saving"
          />
        </div>
        <p v-if="error" class="mt-2 text-sm text-danger-700" role="alert">{{ error }}</p>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto pb-3">
        <section data-testid="current-activity-type-section">
          <h2 class="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            Current
          </h2>
          <button
            type="button"
            class="flex min-h-12 w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-black/[0.04]"
            :class="
              draftType === currentOption.id ? 'bg-emerald-50 text-emerald-800' : 'text-gray-800'
            "
            :disabled="saving"
            :data-testid="
              currentOption.id
                ? `activity-type-option-${currentOption.id}`
                : 'activity-type-option-generic'
            "
            @click="selectType(currentOption.id)"
          >
            <span class="flex-1 text-base">{{ currentOption.name }}</span>
            <CheckIcon v-if="draftType === currentOption.id" class="h-4 w-4 text-emerald-700" />
          </button>
        </section>

        <section v-if="showGenericActivity">
          <h2 class="px-4 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            General
          </h2>
          <button
            type="button"
            class="flex min-h-12 w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-black/[0.04]"
            :class="draftType === '' ? 'bg-emerald-50 text-emerald-800' : 'text-gray-800'"
            :disabled="saving"
            data-testid="activity-type-option-generic"
            @click="selectType('')"
          >
            <span class="flex-1 text-base">Activity</span>
            <CheckIcon v-if="draftType === ''" class="h-4 w-4 text-emerald-700" />
          </button>
        </section>

        <section v-for="group in filteredGroups" :key="group.name">
          <h2 class="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {{ group.name }}
          </h2>
          <button
            v-for="option in group.options"
            :key="option.id"
            type="button"
            class="flex min-h-12 w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-black/[0.04]"
            :class="draftType === option.id ? 'bg-emerald-50 text-emerald-800' : 'text-gray-800'"
            :disabled="saving"
            :data-testid="`activity-type-option-${option.id}`"
            @click="selectType(option.id)"
          >
            <span class="flex-1 text-base">{{ option.name }}</span>
            <CheckIcon v-if="draftType === option.id" class="h-4 w-4 text-emerald-700" />
          </button>
        </section>

        <p
          v-if="!showGenericActivity && filteredGroups.length === 0"
          class="px-4 py-8 text-center text-sm text-gray-500"
        >
          No other activity types found.
        </p>
      </div>
    </div>
  </MobileActionSheet>
</template>
